import { useState } from 'react'
import type { StorefrontProduct } from '@/types'
import ProductImage from '@/components/order/ProductImage'
import { cn } from '@/lib/cn'

/**
 * Product photo gallery for the configurator page, Waly-style: one large
 * frame with a row of clickable thumbnails beneath it when the product has
 * more than one photo. The active thumbnail carries a primary border - a
 * solid accent like CTAs, not the portal's tinted-background recipe.
 *
 * Mount with key={product.id} so the selected index resets between products.
 */
export default function ProductGallery({ product }: { product: StorefrontProduct }) {
  const images = product.images?.length ? product.images : product.image_url ? [product.image_url] : []
  const [selected, setSelected] = useState(0)
  const [broken, setBroken] = useState<Record<number, boolean>>({})

  const index = Math.min(selected, Math.max(0, images.length - 1))
  const current = images[index]
  const showImage = current && !broken[index]

  return (
    <div>
      <div className="box overflow-hidden p-3">
        {showImage ? (
          <img
            src={current}
            alt={product.name}
            className="aspect-[3/4] w-full rounded-xl object-cover"
            onError={() => setBroken((b) => ({ ...b, [index]: true }))}
          />
        ) : (
          // No photos, or the current one 404s: same shirt-icon fallback the
          // rest of the app uses.
          <ProductImage product={{ ...product, image_url: null }} className="aspect-[3/4] w-full rounded-xl" />
        )}
        <span className="absolute right-5 top-5 z-10 rounded-full bg-white/90 px-2.5 py-0.5 text-2xs font-semibold text-foreground shadow-sm backdrop-blur-sm">
          {product.category}
        </span>
      </div>

      {images.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {images.map((path, i) => (
            <button
              key={`${i}-${path}`}
              type="button"
              aria-label={`Image ${i + 1} of ${product.name}`}
              aria-pressed={i === index}
              onClick={() => setSelected(i)}
              className={cn(
                'size-16 overflow-hidden rounded-lg border-2 transition-colors',
                i === index ? 'border-primary' : 'border-foreground/10 hover:border-foreground/30',
              )}
            >
              <img
                src={path}
                alt=""
                className="size-full object-cover"
                onError={(e) => ((e.target as HTMLImageElement).style.opacity = '0.3')}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
